// All user-visible text lives here. Two languages: EN and BG.
// Non-translatable items (URLs, emails, phones, images) remain in data/content.js.

const en = {
  nav: {
    about: "About",
    program: "Program",
    team: "Team",
    faq: "FAQ",
    contact: "Contact",
    cta: "Start a Conversation",
    brand: "Velocitas",
    brandSub: "Business Accelerator",
  },

  hero: {
    overline: "Velocitas · Growth Lab",
    headlinePre: "Growth Lab for",
    headlineHighlight: "Bulgarian",
    headlinePost: "SMEs",
    subheadline: "Structured Growth. Long-Term Alignment.",
    supporting:
      "A six-month executive program for established SMEs generating €250k–€750k in revenue. Designed for founders ready to build structure before scaling.",
    primaryCta: "Schedule Strategic Conversation",
    secondaryCta: "Explore the Journey",
    promiseOverline: "Our promise",
    promiseTagline: "Structure first. Speed follows.",
    metrics: [
      { value: "6", label: "Month executive program" },
      { value: "€250k–€750k", label: "Target SME revenue range" },
      { value: "up to 20%", label: "Optional minority alignment" },
    ],
  },

  about: {
    overline: "Section 01 · Who It Is For",
    headlinePre: "Built for",
    headlineHighlight: "Established Businesses",
    intro: "Growth Lab is designed for:",
    items: [
      "Revenue-generating SMEs",
      "Founder-led companies",
      "Businesses seeking structural clarity",
      "Companies planning disciplined domestic growth",
      "Leaders open to long-term strategic alignment",
    ],
    notList: [
      "This is not a startup accelerator.",
      "This is not early-stage mentorship.",
      "This is structured business architecture.",
    ],
  },

  program: {
    overline: "Section 02 · The Journey",
    headline: "The Six-Month Progression",
    intro:
      "Six deliberate stages — each designed to earn trust, build structure and confirm alignment before any partnership is formalised.",
    stepWord: "STEP",
    steps: [
      {
        number: "01",
        title: "Strategic Conversation",
        caption: "Start with Ambition.",
        description:
          "An initial strategic discussion designed to understand the company's ambitions, current constraints, and potential for structural growth. This conversation helps determine whether the Growth Lab is the right environment for the business and whether mutual expectations are aligned.",
      },
      {
        number: "02",
        title: "Confidential Entry",
        caption: "Trust Before Transparency.",
        description:
          "Participation begins with the signing of a Non-Disclosure Agreement and a participation agreement, ensuring that all business information shared during the program remains strictly confidential. This creates a secure environment where founders can openly discuss strategic, financial, and operational challenges.",
      },
      {
        number: "03",
        title: "Phase I: Structural Foundation",
        caption: "Strength Before Scale.",
        description:
          "The first three months focus on building the structural foundation of the company. Together we analyze the business model, financial structure, and revenue system in order to identify opportunities and structural weaknesses.",
      },
      {
        number: "04",
        title: "3-Month Strategic Review",
        caption: "Continuation Is Intentional.",
        description:
          "At the midpoint of the program, we conduct a structured strategic review evaluating progress, commitment, and long-term alignment between Velocitas and the participating company. If both sides see strategic potential, an equity alignment option may be signed to allow the possibility of deeper partnership in Phase II.",
      },
      {
        number: "05",
        title: "Phase II: Strategic Elevation",
        caption: "Build for What Comes Next.",
        description:
          "The second phase focuses on long-term strategy. This includes organizational structure, strategic positioning, and building a three-year roadmap for sustainable growth.",
      },
      {
        number: "06",
        title: "Final Strategic Alignment",
        caption: "Decision With Clarity.",
        description:
          "The program concludes with a final strategic alignment meeting where we evaluate the long-term direction of the company and the potential for deeper collaboration. If strategic alignment is confirmed, Velocitas may exercise the previously agreed option to become a minority partner and support the company's next phase of growth.",
      },
    ],
    ctaBanner: {
      overline: "Ready to begin",
      line: "Six months. Structured work flow. Long-term alignment.",
      cta: "Start a Conversation",
    },
  },

  howItWorks: {
    overline: "Section 03 · How the Lab Works",
    headlinePre: "A Structured",
    headlineHighlight: "Executive Flow",
    headlinePost: ".",
    rhythmIntro: "Each month includes:",
    rhythm: [
      "Two 3-hour Individual Strategic Sessions",
      "One Founder Strategic 1:1",
      "One Executive Cohort Forum",
    ],
    closing: ["Depth happens privately.", "Perspective happens collectively."],
    footnote:
      "This ensures confidentiality while building strategic intelligence.",
    monthlyPlan: {
      overline: "6-Month Strategic Architecture",
      heading: "Month by Month",
      intro:
        "A structured progression — each month builds the next layer of strategic clarity.",
      months: [
        {
          label: "Month 01",
          title: "Business Model Architecture",
          tagline: "Understanding What You've Built",
          brief:
            "We dissect your current business model to test its resilience, scalability, and dependency risks.",
          detail:
            "Month 1 focuses on structural clarity. We map revenue streams, client concentration, cost rigidity, margin structure, and founder dependency. We identify structural fragilities that may limit scaling and redesign the business logic for sustainability. The outcome is a validated Business Model Architecture Map that becomes the foundation for all future decisions.",
        },
        {
          label: "Month 02",
          title: "Financial Discipline & Margin Logic",
          tagline: "Margin Is Strategy",
          brief:
            "We transform financial data into decision-making architecture and create a sustainable financial framework.",
          detail:
            "This month builds financial control discipline. We construct contribution margin models, identify hidden cost leakage, and establish KPI dashboards aligned with growth. The goal is not accounting optimization, but strategic financial clarity — understanding where profitability is truly generated and where it is exposed.",
        },
        {
          label: "Month 03",
          title: "Revenue Engineering",
          tagline: "Sales Must Be Designed",
          brief: "We redesign your revenue system to reduce unpredictability.",
          detail:
            "We analyze pipeline structure, conversion logic, client acquisition cost, and founder sales dependency. We move from reactive sales activity to structured revenue engineering. By the end of Month 3, the company operates with a measurable and controllable revenue architecture.",
        },
        {
          label: "Checkpoint",
          title: "3-Month Strategic Review",
          tagline: "Continuation Is Intentional",
          brief: "Formal evaluation of structural progress and alignment.",
          detail:
            "Decision: conclude or proceed to Phase II with Equity Alignment Option.",
          isCheckpoint: true,
        },
        {
          label: "Month 04",
          title: "Organizational Evolution",
          tagline: "Growth Requires Structural Leadership",
          brief: "We redesign the organization to support scale.",
          detail:
            "We assess delegation gaps, key role definitions, decision bottlenecks, and management capacity. The focus is transitioning from founder-centric execution to scalable leadership architecture. Outcome: 12–24 month organizational roadmap.",
        },
        {
          label: "Month 05",
          title: "Strategic Positioning & New Markets Assessment",
          tagline: "Ambition Needs Direction",
          brief:
            "We explore structured expansion and positioning opportunities.",
          detail:
            "This month analyzes competitive positioning, differentiation logic, and market expansion pathways. We conduct New Markets opportunity assessment tailored to the Bulgarian context and test domestic vs. international potential. Outcome: Strategic Opportunity Map.",
        },
        {
          label: "Month 06",
          title: "3-Year Strategic Roadmap",
          tagline: "Build Beyond Operations",
          brief: "We consolidate structure into long-term direction.",
          detail:
            "We develop a 3-year structural roadmap integrating finance, organization, revenue systems, and market expansion. Governance readiness and board-level decision thinking are introduced. This month concludes with the Final Strategic Alignment Meeting.",
        },
      ],
    },
    leadCta: {
      overline: "Take it with you",
      headingLine1: "The 6-Month Plan,",
      headingLine2: "in your inbox.",
      body: "A founder-ready PDF outlining every month of the Growth Lab — architecture, financial discipline, revenue, leadership, positioning, and the 3-year roadmap.",
      namePlaceholder: "Your name (optional)",
      emailPlaceholder: "Email address",
      submitIdle: "Download the 6-Month Plan PDF",
      submitLoading: "Preparing your PDF…",
      successHeading: "Your download has started.",
      successBodyPre: "If it didn't,",
      successBodyLink: "click here to download the PDF",
      successBodyPost: ". We'll be in touch shortly.",
      errorInvalid: "Please enter a valid email address.",
      errorGeneric: "Something went wrong. Please try again.",
      footnote:
        "We use your email only to share the plan and occasional Velocitas updates. No spam.",
    },
  },

  alignment: {
    overline: "Section 04 · The Alignment Model",
    headlinePre: "Partnership Is",
    headlineHighlight: "Earned",
    headlinePost: ", Not Assumed.",
    paragraphs: [
      "Phase I is commitment-free.",
      "If continuing to Phase II, an Equity Alignment Option is signed.",
      "At Month 6, if strategic alignment is confirmed, Velocitas may acquire up to 20% minority equity.",
      "If alignment is not formalized, the collaboration concludes professionally with a €2,000 Phase II participation contribution.",
    ],
    pillars: ["No automatic equity.", "No forced partnership."],
  },

  outcomes: {
    overline: "Section 05 · Outcomes",
    headline: "What You Leave With",
    items: [
      "Structural business model clarity",
      "Financial control discipline",
      "Revenue system architecture",
      "Organizational roadmap",
      "3-year strategic growth plan",
      "Potential long-term strategic partner",
    ],
    cta: "Schedule Strategic Conversation",
    ctaFootnote: "Begin with a confidential conversation — no commitment required.",
  },

  manifesto: {
    overline: "The Velocitas Manifesto",
    headlinePre: "The Path to",
    headlineHighlight: "Progress",
    headlinePost: ".",
    lede: "Finance is the oldest journey in human history — a constant search for better ways to move, store, and multiply value. But today, that journey has reached a breaking point.",
    p1: "The friction of legacy systems and the weight of outdated infrastructure are holding back the next great leap in human prosperity.",
    p2Pre: "At Velocitas, we believe that the journey",
    p2Iter: "(Iter)",
    p2Mid: "is just as critical as the destination. We are not a passive knowledge source; we are a high-velocity vehicle for",
    p2Profectus: "Profectus",
    p2Post: " — true, measurable progress.",
    p3Pre: "We have built this Growth Lab for the architects of the new economy. For those who understand that in modern business, speed without security is a liability, and growth without a roadmap is a dead end. We provide the",
    p3Incrementum: "Incrementum",
    p3Post: "you need: not just deep expertise, but the regulatory blueprints, the institutional handshakes, and the technical mentorship required to scale from a disruptive idea to a global standard.",
    closingLine1: "The road to the future of business is complex.",
    closingHighlight: "Don't walk it alone.",
    closingPost: "Drive it with Velocitas.",
    terms: [
      { word: "Iter", meaning: "The Journey" },
      { word: "Profectus", meaning: "Measurable Progress" },
      { word: "Incrementum", meaning: "Structured Growth" },
    ],
  },

  team: {
    overline: "The Founders",
    headline: "Senior advisors. Operating partners.",
    intro:
      "Velocitas is led by a team with complementary depth in financial strategy and operational transformation — engaged directly on every mandate.",
    roles: {
      margarit: "Co-Founder & Strategic Lead",
      vasil: "Co-Founder & Chief Operating Officer",
    },
    bios: {
      margarit: [
        "Margarit Arnaudov is a seasoned business and financial advisor with over 15 years of experience supporting companies across multiple sectors including financial services, energy, real estate, construction, and agriculture.",
        "Margarit has deep expertise in capital structuring, risk management, financing strategy, and performance reporting, helping business leaders transform complex financial and operational challenges into strategic decision frameworks.",
      ],
      vasil: [
        "Vasil Vezenkov is a seasoned business transformation and operations leader with over 15 years of experience in applying strategic frameworks to complex enterprise environments, particularly in technology and systems delivery.",
        "Vasil has developed a strong track record in optimizing business processes, driving operational efficiency and aligning technology with long-term commercial goals.",
      ],
    },
  },

  faq: {
    overline: "Frequently Asked Questions",
    headlinePre: "Answers, organised by",
    headlineHighlight: "what matters",
    headlinePost: ".",
    intro:
      "A complete reference covering eligibility, structure, equity, confidentiality and logistics.",
    categories: [
      {
        id: "program-basics",
        title: "Program Basics",
        items: [
          { q: "What is Velocitas Growth Lab?", a: "Velocitas Growth Lab is a six-month strategic growth program for established small and medium-sized businesses. It helps founders strengthen their business model, financial structure and strategic direction through a structured executive process. In some cases, Velocitas may later become a long-term minority partner in companies where strong alignment exists." },
          { q: "Why are you doing this?", a: "Velocitas was created to support the next generation of Bulgarian SMEs that have strong potential but need structured strategic guidance to scale sustainably. Our goal is to help founders build stronger, more resilient businesses and, in selected cases, form long-term partnerships." },
          { q: "Is this a typical accelerator?", a: "No. Velocitas is not a startup accelerator. The program is designed for existing companies, not early-stage startup ideas. The focus is on structural growth, financial discipline, and long-term strategy." },
        ],
      },
      {
        id: "eligibility",
        title: "Eligibility",
        items: [
          { q: "How do I know if my company is a good fit?", a: "The program is designed for companies that already have an operating business and are looking to grow in a more structured and strategic way. Typical participants are founder-led SMEs that want to improve profitability, build scalable processes and prepare for long-term expansion." },
          { q: "What are the eligibility criteria?", a: "We typically look for companies that: have an operating business model; have active clients or revenue streams; are led by founders committed to strategic growth; are open to transparent discussion about business structure and challenges." },
          { q: "Can I join if I only have an idea?", a: "No. Velocitas Growth Lab is not designed for idea-stage startups. The program works best with companies that already have products, services, or clients in the market." },
          { q: "Can we join if we are just two co-founders?", a: "Yes. Many successful companies start with small founding teams. What matters most is having a real business and the commitment to develop it further." },
          { q: "Do we need to have a registered legal entity?", a: "Yes. Participating companies must operate through a legally registered entity such as an LLC or similar business structure." },
          { q: "Do we need to already be profitable?", a: "Profitability is not strictly required, but the company should have a working business model and active operations." },
        ],
      },
      {
        id: "program-structure",
        title: "Program Structure",
        items: [
          { q: "What happens during the first 90 days?", a: "The first three months focus on building the structural foundation of the company. Together we analyze the business model, financial structure, and revenue system in order to identify opportunities and structural weaknesses." },
          { q: "What happens during the second phase of the program?", a: "The second phase focuses on long-term strategy. This includes organizational structure, strategic positioning, and building a three-year roadmap for sustainable growth." },
          { q: "What happens after the six months?", a: "At the end of the program we conduct a final strategic alignment meeting. If both sides see strong long-term potential, Velocitas may decide to become a minority shareholder and support the company's continued growth." },
          { q: "What results should we expect from the program?", a: "Companies typically finish the program with: a clear business model architecture; stronger financial visibility; a structured revenue strategy; a three-year strategic roadmap." },
          { q: "How often do meetings take place?", a: "Each month includes: two individual working sessions; one one-to-one meeting with the founder; one cohort session with other participating companies. This creates a consistent executive cadence throughout the program." },
        ],
      },
      {
        id: "equity-partnership",
        title: "Equity & Partnership",
        items: [
          { q: "Do you take equity in every company?", a: "No. Equity participation only happens if both sides see strong long-term strategic alignment." },
          { q: "What percentage of equity may Velocitas take?", a: "If a partnership is agreed, Velocitas may acquire up to 20% minority ownership in the company. The exact structure is discussed individually and only after the program." },
          { q: "What type of equity is involved?", a: "Velocitas only considers minority ownership positions. The goal is to support growth and alignment, not to control or acquire the company." },
          { q: "Can Velocitas acquire the entire company?", a: "No. Velocitas does not pursue majority ownership or full acquisitions. Our model focuses on minority strategic partnerships." },
          { q: "Can we cancel the equity option after the three-month review?", a: "Yes. The equity option signed after the three-month review only gives Velocitas the possibility to evaluate a partnership later. The founders always have the right not to proceed." },
          { q: "If we decide not to continue the partnership, what happens?", a: "If the equity option is not exercised after the program, the company simply continues independently. A small participation contribution (€2,000) will apply only in case the equity option is rejected by the participating company." },
        ],
      },
      {
        id: "confidentiality",
        title: "Confidentiality",
        items: [
          { q: "How is our company information protected?", a: "Before the program begins, we sign a Non-Disclosure Agreement (NDA) and a participation agreement. This ensures that all business information shared during the program remains strictly confidential." },
          { q: "How do we know our information will not be shared with competitors?", a: "Confidentiality is a core principle of the program. We work with companies from different sectors and treat all shared information as strictly private under the signed NDA." },
          { q: "How do we know you will not use our idea to start a competing business?", a: "Velocitas does not build competing companies. Our role is to support existing businesses, not replicate them. All strategic discussions are protected under confidentiality agreements." },
        ],
      },
      {
        id: "program-logistics",
        title: "Program Logistics",
        items: [
          { q: "Is the Programme remote or in-person?", a: "We operate a hybrid mode. Meetings and workshops can be held in person at our office, while some sessions may also take place online depending on schedule and availability." },
          { q: "How can we contact you?", a: "You can reach us through the contact details on our website or request a Strategic Conversation with the Velocitas team." },
          { q: "Who can sign the participation agreement?", a: "The agreement should be signed by the company owners or authorized representatives who are responsible for strategic decisions." },
        ],
      },
    ],
  },

  contact: {
    overline: "Get in touch",
    headline: "Let's structure your next phase of growth.",
    intro:
      "Reach out directly to either co-founder. We respond personally — usually within one business day — and start with a focused diagnostic conversation.",
    emailLabel: "Email",
    phoneLabel: "Phone",
    footerOffice: "Velocitas — Business Accelerator · Sofia, str. Ekzarh Yosif 127",
    footerNote: "By appointment · Founder-led engagements",
  },

  footer: {
    blurb:
      "Acceleration through financial discipline, structural clarity and long-term value creation.",
    navigateLabel: "Navigate",
    officeLabel: "Office",
    officeText: "Sofia, str. Ekzarh Yosif 127",
    officeByAppt: "By appointment only",
    copyright: "All rights reserved.",
    tagline: "Structure first · Speed follows",
    privacy: "Privacy Policy",
  },

  privacy: {
    back: "Back to Velocitas",
    overline: "Legal · Privacy Policy",
    headlinePre: "Mountain Gem Investment LLC",
    headlineHighlight: "Privacy Policy",
    p1: "Mountain Gem Investment LLC receives, collects and stores any information you enter on our website or provide us in any other way. In addition, we collect the Internet protocol (IP) address used to connect your computer to the Internet; login; e-mail address; password; and computer and connection information. We may use software tools to measure and collect session information, including page response times, length of visits to certain pages, page interaction information, and methods used to browse away from the page. We also collect personally identifiable information (including name, email, password, communications); comments, feedback, product reviews, recommendations, and personal profile. This information is being collected via cookies.",
    p2: "We collect such Non-personal and Personal Information for the following purposes:",
    list: [
      "To market and operate our products and services;",
      "To provide our Users with ongoing customer assistance and technical support;",
      "To be able to contact our Visitors and Users with general or personalized service-related notices and promotional messages;",
      "To create aggregated statistical data and other aggregated and/or inferred Non-personal Information, which we or our business partners may use to provide and improve our respective services;",
      "To comply with any applicable laws and regulations.",
    ],
    p3: "When you consent to the use of cookies and visit, register or conduct any other activity on our website, as part of the process, we collect personal information you give us such as your name, address and email address. Your personal information will be used for the specific reasons stated above only. If you would like to: access, correct, amend or delete any personal information we have about you, you are invited to contact us via the provided contact forms on our website.",
    p4: "Mountain Gem Investment LLC reserves the right to modify this privacy policy at any time, so please review it frequently. Changes and clarifications will take effect immediately upon their posting on the website. If we make material changes to this policy, we will notify you here that it has been updated, so that you are aware of what information we collect, how we use it, and under what circumstances, if any, we use and/or disclose it.",
  },
};

const bg = {
  nav: {
    about: "За нас",
    program: "Програма",
    team: "Екип",
    faq: "ЧЗВ",
    contact: "Контакти",
    cta: "Започнете разговор",
    brand: "Velocitas",
    brandSub: "Business Accelerator",
  },

  hero: {
    overline: "Велоситас · Лаборатория за Растеж",
    headlinePre: "Лаборатория за Растеж за",
    headlineHighlight: "Българския",
    headlinePost: "Среден Бизнес",
    subheadline: "Структуриран растеж. Дългосрочно партньорство.",
    supporting:
      "Шестмесечна програма за утвърдени малки и средни предприятия с приходи между €250к и €750к. Създадена за основатели, готови да изградят структура преди следващата стъпка.",
    primaryCta: "Запишете стратегически разговор",
    secondaryCta: "Разгледайте програмата",
    promiseOverline: "Нашето обещание",
    promiseTagline: "Първо структура. После растеж.",
    metrics: [
      { value: "6", label: "Месечна програма" },
      { value: "€250к–€750к", label: "Целеви приходи на МСП" },
      { value: "до 20%", label: "Опционално миноритарно участие" },
    ],
  },

  about: {
    overline: "Секция 01 · За кого е",
    headlinePre: "Създадена за",
    headlineHighlight: "утвърдени компании",
    intro: "Велоситас Лаборатория за Растеж е създадена за:",
    items: [
      "Среден Бизнес с реални приходи",
      "Компании, водени от основателите си",
      "Бизнеси, търсещи структурна яснота",
      "Компании, планиращи дисциплиниран растеж на вътрешния пазар",
      "Лидери, отворени за дългосрочно стратегическо партньорство",
    ],
    notList: [
      "Това не е startup акселератор.",
      "Това не е менторство за ранен етап.",
      "Това е структурирана бизнес архитектура.",
    ],
  },

  program: {
    overline: "Секция 02 · Пътят",
    headline: "Шестмесечно пътешествие",
    intro:
      "Шест целенасочени етапа — всеки създаден да изгражда доверие, структура и потвърждение на партньорството преди формализирането му.",
    stepWord: "ЕТАП",
    steps: [
      {
        number: "01",
        title: "Стратегически разговор",
        caption: "Започнете с амбиция.",
        description:
          "Първоначален стратегически разговор, който има за цел да разбере амбициите на компанията, текущите ограничения и потенциала за структурен растеж. Този разговор помага да се определи дали Велоситас Лаборатория за Растеж е правилната среда за бизнеса и дали взаимните очаквания съвпадат.",
      },
      {
        number: "02",
        title: "Конфиденциално участие",
        caption: "Доверие преди прозрачност.",
        description:
          "Участието започва с подписване на Споразумение за неразкриване на информация (NDA) и договор за участие, които гарантират, че цялата бизнес информация, споделена по време на програмата, остава строго конфиденциална. Това създава защитена среда, в която основателите могат открито да обсъждат стратегически, финансови и оперативни предизвикателства.",
      },
      {
        number: "03",
        title: "Фаза I: Структурна основа",
        caption: "Основа преди растеж.",
        description:
          "Първите три месеца са фокусирани върху изграждането на структурната основа на компанията. Заедно анализираме бизнес модела, финансовата структура и системата на приходите, за да идентифицираме възможности и структурни слабости.",
      },
      {
        number: "04",
        title: "Стратегически преглед на 3-ия месец",
        caption: "Продължението е съзнателно.",
        description:
          "На средата на програмата правим структуриран стратегически преглед, оценяващ напредъка, ангажираността и дългосрочното съответствие между Velocitas и участващата компания. Ако и двете страни виждат стратегически потенциал, може да бъде подписана опция за дялово партньорство, която отваря възможността за по-задълбочено сътрудничество във Фаза II.",
      },
      {
        number: "05",
        title: "Фаза II: Стратегическо издигане",
        caption: "Подготовка за следващото ниво.",
        description:
          "Втората фаза е фокусирана върху дългосрочна стратегия. Това включва организационна структура, стратегическо позициониране и изграждане на тригодишна пътна карта за устойчив растеж.",
      },
      {
        number: "06",
        title: "Финален стратегически разговор",
        caption: "Решение с яснота.",
        description:
          "Програмата завършва с финална среща за стратегическа посока, в която оценяваме дългосрочната перспектива на компанията и потенциала за по-дълбоко сътрудничество. Ако общата стратегическа посока бъде потвърдена, Velocitas може да упражни предварително договорената опция и да стане миноритарен партньор, подкрепящ следващата фаза на растеж.",
      },
    ],
    ctaBanner: {
      overline: "Готови да започнете",
      line: "Шест месеца. Структуриран работен ритъм. Дългосрочно партньорство.",
      cta: "Започнете разговор",
    },
  },

  howItWorks: {
    overline: "Секция 03 · Как работи лабораторията",
    headlinePre: "Структуриран",
    headlineHighlight: "изпълнителен ритъм",
    headlinePost: ".",
    rhythmIntro: "Всеки месец включва:",
    rhythm: [
      "Две индивидуални стратегически сесии по 3 часа",
      "Една стратегическа среща 1:1 с основателя",
      "Един съвместен форум с другите участници",
    ],
    closing: ["Дълбочината се случва насаме.", "Перспективата — заедно."],
    footnote:
      "Това осигурява конфиденциалност, докато се изгражда стратегическа интелигентност.",
    monthlyPlan: {
      overline: "6-месечна стратегическа архитектура",
      heading: "Месец по месец",
      intro:
        "Структурирано развитие — всеки месец надгражда следващото ниво стратегическа яснота.",
      months: [
        {
          label: "Месец 01",
          title: "Архитектура на бизнес модела",
          tagline: "Разбиране на изграденото",
          brief:
            "Анализираме настоящия бизнес модел, за да тестваме неговата устойчивост, възможности за растеж и зависимости.",
          detail:
            "Месец 1 е фокусиран върху структурна яснота. Картографираме приходните потоци, концентрацията на клиенти, разходна и маржинова структура и зависимостта от основателя. Идентифицираме структурните слабости, които биха могли да ограничат възможностите за растеж, и препроектираме бизнес логиката за устойчивост. Резултатът е валидирана Карта на бизнес модела, която става основа на всички бъдещи решения.",
        },
        {
          label: "Месец 02",
          title: "Финансова дисциплина и логика на маржа",
          tagline: "Маржът е стратегия",
          brief:
            "Превръщаме финансовите данни в архитектура за вземане на решения и създаваме устойчива финансова рамка.",
          detail:
            "Този месец въвежда дисциплина на финансовия контрол. Изграждаме модели на дейностите носещи марж, идентифицираме скрити разходи и установяваме ключови показатели, съгласувани с растежа. Целта не е счетоводна оптимизация, а стратегическа финансова яснота — разбиране откъде наистина се генерира печалбата и къде е изложена на риск.",
        },
        {
          label: "Месец 03",
          title: "Инженеринг на приходите",
          tagline: "Продажбите се нуждаят от дизайн",
          brief:
            "Препроектираме приходната Ви система, за да намалим непредсказуемостта.",
          detail:
            "Анализираме структурата на бъдещите продажби, логиката на конверсията, разходите за привличане на клиенти и зависимостта на продажбите от основателя. Преминаваме от реактивна продажбена активност към структуриран инженеринг на приходите. До края на Месец 3 компанията оперира с измерима и контролируема приходна архитектура.",
        },
        {
          label: "Контролна точка",
          title: "Стратегически преглед на 3-ия месец",
          tagline: "Продължението е съзнателно",
          brief: "Формална оценка на структурния напредък и стратегическа посока.",
          detail:
            "Решение: приключване или преминаване към Фаза II с опция за дялово партньорство.",
          isCheckpoint: true,
        },
        {
          label: "Месец 04",
          title: "Организационна еволюция",
          tagline: "Растежът изисква структурно лидерство",
          brief: "Препроектираме организацията, за да подкрепи възможностите за растеж.",
          detail:
            "Оценяваме празнините в делегирането, дефинициите на ключовите роли, бариерите при вземане на решения и управленския капацитет. Фокусът е върху прехода от изпълнение, центрирано около основателя, към лидерска архитектура фокусирана върху възможностите за растеж. Резултат: организационна пътна карта за 12–24 месеца.",
        },
        {
          label: "Месец 05",
          title: "Стратегическо позициониране и оценка на нови пазари",
          tagline: "Амбицията се нуждае от посока",
          brief:
            "Изследваме структурирани възможности за разширяване и позициониране.",
          detail:
            "Този месец анализира конкурентното позициониране, логиката на диференциация и пътищата за пазарна експанзия. Провеждаме оценка на възможностите за нови пазари, съобразена с българския контекст, и тестваме потенциала на вътрешен срещу международен пазар. Резултат: Карта на стратегическите възможности.",
        },
        {
          label: "Месец 06",
          title: "3-годишна стратегическа пътна карта",
          tagline: "Надграждане отвъд операциите",
          brief: "Консолидираме структурата в дългосрочна посока.",
          detail:
            "Разработваме 3-годишна структурна пътна карта, интегрираща финанси, организация, приходни системи и пазарна експанзия. Въвеждаме готовност за управление и стратегическо мислене. Този месец завършва с Финалната среща за обща стратегическа посока.",
        },
      ],
    },
    leadCta: {
      overline: "Вземете го със себе си",
      headingLine1: "6-месечният план,",
      headingLine2: "във Вашия имейл.",
      body: "Готов за основатели PDF, описващ всеки месец от Велоситас Лаборатория за Растеж — архитектура, финансова дисциплина, приходи, лидерство, позициониране и 3-годишна пътна карта.",
      namePlaceholder: "Вашето име (по избор)",
      emailPlaceholder: "Имейл адрес",
      submitIdle: "Изтеглете 6-месечния план (PDF)",
      submitLoading: "Подготвяме Вашия PDF…",
      successHeading: "Изтеглянето започна.",
      successBodyPre: "Ако не е започнало,",
      successBodyLink: "натиснете тук, за да изтеглите PDF",
      successBodyPost: ". Скоро ще се свържем с Вас.",
      errorInvalid: "Моля, въведете валиден имейл адрес.",
      errorGeneric: "Нещо се обърка. Моля, опитайте отново.",
      footnote:
        "Използваме Вашия имейл единствено за изпращане на плана и редки актуализации от Velocitas. Без спам.",
    },
  },

  alignment: {
    overline: "Секция 04 · Моделът на партньорство",
    headlinePre: "Партньорството се",
    headlineHighlight: "заслужава",
    headlinePost: ", не се подразбира.",
    paragraphs: [
      "Фаза I е без ангажимент.",
      "При продължаване към Фаза II се подписва Опция за дялово партньорство.",
      "На Месец 6, ако стратегическата обща посока бъде потвърдена, Velocitas може да придобие до 20% миноритарно дялово участие.",
      "Ако общата стратегическа посока не бъде формализирана, сътрудничеството приключва с такса за участие във Фаза II от €2,000.",
    ],
    pillars: ["Без автоматично дялово участие.", "Без принудително партньорство."],
  },

  outcomes: {
    overline: "Секция 05 · Резултати",
    headline: "С какво си тръгвате",
    items: [
      "Структурна яснота на бизнес модела",
      "Дисциплина на финансовия контрол",
      "Архитектура на приходната система",
      "Организационна пътна карта",
      "3-годишен стратегически план за растеж",
      "Потенциален дългосрочен стратегически партньор",
    ],
    cta: "Запишете стратегически разговор",
    ctaFootnote: "Започнете с конфиденциален разговор — без ангажимент.",
  },

  manifesto: {
    overline: "Манифестът на Velocitas",
    headlinePre: "Пътят към",
    headlineHighlight: "прогреса",
    headlinePost: ".",
    lede: "Бизнесът е най-старото пътешествие в човешката история — постоянно търсене на по-добри начини да се движи, съхранява и умножава стойност. Но днес това пътешествие е достигнало повратна точка.",
    p1: "Натискът на остарели системи и тежестта на отживяла инфраструктура възпират следващия голям скок на човешкия просперитет.",
    p2Pre: "Във Velocitas вярваме, че пътешествието",
    p2Iter: "(Iter)",
    p2Mid: "е също толкова важно, колкото и крайната цел. Ние не сме пасивен източник на знание; ние сме високоскоростно превозно средство към",
    p2Profectus: "Profectus",
    p2Post: " — истински, измерим прогрес.",
    p3Pre: "Изградихме тази Лаборатория за Растеж за архитектите на новата икономика. За тези, които разбират, че в съвременния бизнес скоростта без сигурност е риск, а растежът без пътна карта е задънена улица. Ние предоставяме",
    p3Incrementum: "Incrementum",
    p3Post: ", от който се нуждаете: не просто дълбока експертиза, а регулаторни модели, институционални контакти и техническо менторство, необходими за растеж от иновативна идея до глобален стандарт.",
    closingLine1: "Пътят към бъдещето на бизнеса е сложен.",
    closingHighlight: "Не вървете сами.",
    closingPost: "Вървете с Velocitas.",
    terms: [
      { word: "Iter", meaning: "Пътешествието" },
      { word: "Profectus", meaning: "Измерим прогрес" },
      { word: "Incrementum", meaning: "Структуриран растеж" },
    ],
  },

  team: {
    overline: "Основателите",
    headline: "Опитни съветници. Оперативни партньори.",
    intro:
      "Velocitas се ръководи от екип с допълваща се експертиза във финансовата стратегия и оперативната трансформация — ангажиран лично с всеки участник.",
    roles: {
      margarit: "Съосновател и стратегически директор",
      vasil: "Съосновател и оперативен директор",
    },
    bios: {
      margarit: [
        "Маргарит Арнаудов е опитен бизнес и финансов съветник с над 15 години опит, подкрепящ компании в различни сектори, включително финансови услуги, енергетика, недвижими имоти, строителство и селско стопанство.",
        "Маргарит има задълбочена експертиза в структурирането на капитал, управлението на риска, финансовата стратегия и отчитането на резултати, помагайки на бизнес лидерите да превърнат сложни финансови и оперативни предизвикателства в стратегически рамки за вземане на решения.",
      ],
      vasil: [
        "Васил Везенков е опитен лидер в бизнес трансформацията и операциите с над 15 години опит в прилагането на стратегически рамки в сложни корпоративни среди, включително технологиите и системната архитектура.",
        "Васил е изградил богата репутация в оптимизирането на бизнес процеси, повишаването на оперативната ефективност и свързването на технологиите с дългосрочни търговски цели.",
      ],
    },
  },

  faq: {
    overline: "Често задавани въпроси",
    headlinePre: "Отговори, организирани по",
    headlineHighlight: "темите, които Ви интересуват",
    headlinePost: ".",
    intro:
      "Пълен списък, обхващащ допустимост, структура, дялово участие, конфиденциалност и логистика.",
    categories: [
      {
        id: "program-basics",
        title: "Основи на програмата",
        items: [
          { q: "Какво е Велоситас Лаборатория за Растеж?", a: "Велоситас Лаборатория за Растеж е шестмесечна стратегическа програма за растеж за утвърдени малки и средни компании. Тя помага на основателите да укрепят своя бизнес модел, финансова структура и стратегическа посока чрез структуриран изпълнителен процес. В някои случаи Velocitas може впоследствие да стане дългосрочен миноритарен партньор в компании, с които има обща стратегическа посока." },
          { q: "Защо го правите?", a: "Velocitas е създадена, за да подкрепя следващото поколение български МСП с висок потенциал, които се нуждаят от структурирано стратегическо ръководство, за да растат устойчиво. Нашата цел е да помогнем на основателите да изградят по-силни и устойчиви бизнеси и, в избрани случаи, да формираме дългосрочни партньорства." },
          { q: "Това типичен акселератор ли е?", a: "Не. Velocitas не е startup акселератор. Програмата е създадена за съществуващи компании, а не за идеи в ранен етап. Фокусът е върху структурен растеж, финансова дисциплина и дългосрочна стратегия." },
        ],
      },
      {
        id: "eligibility",
        title: "Допустимост",
        items: [
          { q: "Как да разбера дали компанията ми е подходяща?", a: "Програмата е създадена за компании, които вече имат действащ бизнес и искат да растат по по-структуриран и стратегически начин. Типичните участници са МСП, ръководени от основателите си, които искат да подобрят рентабилността, да изградят стабилни процеси и да се подготвят за дългосрочно разрастване." },
          { q: "Какви са критериите за допустимост?", a: "Обикновено търсим компании, които: имат действащ бизнес модел; имат активни клиенти или приходни потоци; се ръководят от основатели, ангажирани със стратегически растеж; са отворени за прозрачно обсъждане на бизнес структурата и предизвикателствата." },
          { q: "Мога ли да участвам, ако имам само идея?", a: "Не. Велоситас Лаборатория за Растеж не е създадена за стартъпи на ниво идея. Програмата работи най-добре с компании, които вече имат продукти, услуги или клиенти на пазара." },
          { q: "Можем ли да участваме, ако сме само двама съоснователи?", a: "Да. Много успешни компании започват с малки основателски екипи. Най-важно е да има реален бизнес и ангажимент за неговото развитие." },
          { q: "Трябва ли да имаме регистрирано юридическо лице?", a: "Да. Участващите компании трябва да оперират чрез законно регистрирано юридическо лице, като ООД или подобна бизнес структура." },
          { q: "Трябва ли вече да сме рентабилни?", a: "Рентабилността не е строго изискване, но компанията трябва да има работещ бизнес модел и активни операции." },
        ],
      },
      {
        id: "program-structure",
        title: "Структура на програмата",
        items: [
          { q: "Какво се случва през първите 90 дни?", a: "Първите три месеца са фокусирани върху изграждането на структурната основа на компанията. Заедно анализираме бизнес модела, финансовата структура и системата на приходите, за да идентифицираме възможности и структурни слабости." },
          { q: "Какво се случва през втората фаза на програмата?", a: "Втората фаза е фокусирана върху дългосрочна стратегия. Това включва организационна структура, стратегическо позициониране и изграждане на тригодишна пътна карта за устойчив растеж." },
          { q: "Какво се случва след шестте месеца?", a: "В края на програмата провеждаме финална среща за проверка наличие на обща стратегическа посока. Ако и двете страни виждат силен дългосрочен потенциал, Velocitas може да реши да стане миноритарен акционер и да подкрепи продължаващия растеж на компанията." },
          { q: "Какви резултати трябва да очакваме от програмата?", a: "Компаниите обикновено завършват програмата с: ясна архитектура на бизнес модела; по-добра финансова видимост; структурирана стратегия за приходи; тригодишна стратегическа пътна карта." },
          { q: "Колко често се провеждат срещите?", a: "Всеки месец включва: две индивидуални работни сесии; една среща 1:1 с основателя; един съвместен форум с другите участващи компании. Това създава последователен изпълнителен ритъм през цялата програма." },
        ],
      },
      {
        id: "equity-partnership",
        title: "Дялово участие и партньорство",
        items: [
          { q: "Взимате ли дялово участие във всяка компания?", a: "Не. Дялово участие се случва само ако и двете страни виждат силна дългосрочна стратегическа посока." },
          { q: "Какъв процент дялово участие може да поеме Velocitas?", a: "При договорено партньорство Velocitas може да придобие до 20% миноритарно участие в компанията. Точната структура се обсъжда индивидуално и само след програмата." },
          { q: "Какъв тип дялово участие се обмисля?", a: "Velocitas разглежда само миноритарни позиции на собственост. Целта е да подкрепя растежа и стратегията, а не да контролира или придобива компанията." },
          { q: "Може ли Velocitas да придобие цялата компания?", a: "Не. Velocitas не цели мажоритарна собственост или пълни придобивания. Нашият модел е фокусиран върху миноритарни стратегически партньорства." },
          { q: "Можем ли да откажем дяловата опция след прегледа на третия месец?", a: "Да. Опцията за дялово участие, подписана след тримесечния преглед, дава на Velocitas единствено възможността да оцени партньорството по-късно. Основателите винаги имат право да не продължат." },
          { q: "Ако решим да не продължим партньорството, какво се случва?", a: "Ако дяловата опция не бъде упражнена след програмата, компанията просто продължава самостоятелния си път. Малка такса за участие (€2,000) се прилага само ако дяловата опция бъде отхвърлена от участващата компания." },
        ],
      },
      {
        id: "confidentiality",
        title: "Конфиденциалност",
        items: [
          { q: "Как е защитена информацията за нашата компания?", a: "Преди началото на програмата подписваме Споразумение за неразкриване на информация (NDA) и договор за участие. Това гарантира, че цялата бизнес информация, споделена по време на програмата, остава строго конфиденциална." },
          { q: "Как ще знаем, че нашата информация няма да бъде споделена с конкуренти?", a: "Конфиденциалността е основен принцип на програмата. Работим с компании от различни сектори и третираме цялата споделена информация като напълно поверителна, защитена с NDA." },
          { q: "Как ще знаем, че няма да използвате идеята ни, за да създадете конкурентен бизнес?", a: "Velocitas не изгражда конкурентни компании. Нашата роля е да подкрепяме съществуващи бизнеси, а не да ги възпроизвеждаме. Всички стратегически разговори са защитени от споразумения за конфиденциалност." },
        ],
      },
      {
        id: "program-logistics",
        title: "Логистика на програмата",
        items: [
          { q: "Програмата онлайн ли е или присъствено?", a: "Работим в хибриден режим. Срещите и работните сесии могат да се провеждат лично в нашия офис, докато някои сесии може да са онлайн в зависимост от графика и натовареността на участниците." },
          { q: "Как можем да се свържем с вас?", a: "Можете да ни намерите чрез контактите на нашия уебсайт или да заявите Стратегически разговор с екипа на Velocitas." },
          { q: "Кой може да подпише договора за участие?", a: "Договорът трябва да бъде подписан от собствениците на компанията или упълномощени представители, отговорни за стратегическите решения." },
        ],
      },
    ],
  },

  contact: {
    overline: "Свържете се с нас",
    headline: "Нека структурираме следващата Ви фаза на растеж.",
    intro:
      "Свържете се директно с някой от съоснователите. Отговаряме лично — обикновено в рамките на един работен ден — и започваме с фокусиран диагностичен разговор.",
    emailLabel: "Имейл",
    phoneLabel: "Телефон",
    footerOffice:
      "Velocitas — Бизнес акселератор · София, ул. Екзарх Йосиф 127",
    footerNote: "След предварителна уговорка · Срещи, водени от основателите",
  },

  footer: {
    blurb:
      "Ускоряване чрез финансова дисциплина, структурна яснота и създаване на дългосрочна стойност.",
    navigateLabel: "Навигация",
    officeLabel: "Офис",
    officeText: "София, ул. Екзарх Йосиф 127",
    officeByAppt: "Само след предварителна уговорка",
    copyright: "Всички права запазени.",
    tagline: "Първо структура · После растеж",
    privacy: "Политика за поверителност",
  },

  privacy: {
    back: "Обратно към Velocitas",
    overline: "Правна информация · Политика за поверителност",
    headlinePre: "Mountain Gem Investment LLC",
    headlineHighlight: "Политика за поверителност",
    p1: "Mountain Gem Investment LLC получава, събира и съхранява всяка информация, която въвеждате на нашия уебсайт или ни предоставяте по друг начин. В допълнение събираме IP адреса, използван за свързване на Вашия компютър с интернет; потребителско име; имейл адрес; парола; както и информация за устройството и връзката. Може да използваме софтуерни инструменти за измерване и събиране на информация за сесията, включително време за отговор на страниците, продължителност на посещенията, взаимодействия със страниците и методи на излизане. Също така събираме лични данни (включително име, имейл, парола, комуникации); коментари, обратна връзка, отзиви за продукти, препоръки и личен профил. Тази информация се събира чрез бисквитки.",
    p2: "Събираме такава неперсонална и лична информация за следните цели:",
    list: [
      "За маркетинг и оперативно функциониране на нашите продукти и услуги;",
      "За предоставяне на постоянна клиентска и техническа поддръжка на нашите потребители;",
      "За да можем да се свържем с нашите посетители и потребители с общи или персонализирани известия и промоционални съобщения, свързани с услугата;",
      "За създаване на агрегирани статистически данни и друга агрегирана и/или изведена неперсонална информация, която ние или нашите бизнес партньори можем да използваме за предоставяне и подобряване на съответните си услуги;",
      "За съответствие с приложимите закони и наредби.",
    ],
    p3: "Когато се съгласите с използването на бисквитки и посетите, регистрирате се или извършвате друга дейност на нашия уебсайт, като част от процеса събираме лични данни, които ни предоставяте — име, адрес и имейл. Вашите лични данни ще се използват само за конкретните цели, посочени по-горе. Ако искате да: получите достъп, коригирате, измените или изтриете лични данни, които съхраняваме за Вас, моля свържете се с нас чрез предоставените контактни форми на нашия уебсайт.",
    p4: "Mountain Gem Investment LLC си запазва правото да променя тази политика за поверителност по всяко време, затова моля преглеждайте я редовно. Промените и пояснителните бележки влизат в сила незабавно при публикуването им на уебсайта. Ако направим съществени промени в тази политика, ще Ви уведомим тук, за да сте информирани каква информация събираме, как я използваме и при какви обстоятелства, ако има такива, я използваме и/или разкриваме.",
  },
};

export const translations = { en, bg };
